/**
 * Méta-progression et économie d'éclats de la Descente du sanctuaire.
 *
 * ⚠ Écrit en `.mjs` **volontairement** : client, serveur et tests importent ce fichier-ci.
 * Tout ce qui est ici pilote une monnaie (coût des améliorations, récompense d'une run) et
 * est revalidé côté serveur — une table dupliquée qui dérive, c'est un exploit. Les types
 * vivent dans `descente_sanctuaire_meta.d.mts`.
 */

/** Bonus d'éclats de la première descente du jour. */
export const DESCENTE_DAILY_SHARD_MULT = 1.5

/** Multiplicateurs de récompense par ascension / preset (miroir des tables de run). */
export const DESCENTE_ASCENSION_SHARD_MULT = { 0: 1, 1: 1.2, 2: 1.4 }
export const DESCENTE_PRESET_SHARD_MULT = { safe_live: 1, hardcore_event: 1.3 }

export const DESCENTE_KNOWN_MODIFIERS = [
  'dps_global_15',
  'first_hit_crit',
  'revive_plus1',
  'run_shard_20',
  'heal_between',
  'glass_cannon_30',
  'timer_bonus_6',
  'insurance_heart',
]

/** Clé stats portant les niveaux d'améliorations permanentes. */
export const SANCT_UPGRADES_STATS_KEY = '_sanctUpgrades'
/** Clé stats portant le résultat de la descente du jour (classement quotidien). */
export const SANCT_DAILY_STATS_KEY = '_sanctDaily'

/**
 * Améliorations permanentes achetées en éclats. Coût du niveau N = baseCost × growth^N,
 * donc chaque niveau coûte davantage : la boutique ne s'épuise plus en cinq runs.
 */
export const SANCT_UPGRADES = [
  {
    id: 'shard_yield',
    maxLevel: 10,
    baseCost: 60,
    costGrowth: 1.5,
    /** +8 % d'éclats par niveau. */
    step: 0.08,
    tag: 'economy',
  },
  {
    id: 'descent_power',
    maxLevel: 10,
    baseCost: 75,
    costGrowth: 1.5,
    /** +6 % de dégâts en descente par niveau. */
    step: 0.06,
    tag: 'dps',
  },
  {
    id: 'max_heart',
    maxLevel: 3,
    baseCost: 220,
    costGrowth: 3,
    /** +1 cœur maximum par niveau. */
    step: 1,
    tag: 'survival',
  },
  {
    id: 'draft_reroll',
    maxLevel: 3,
    baseCost: 110,
    costGrowth: 2.4,
    /** +1 reroll de draft par niveau. */
    step: 1,
    tag: 'survival',
  },
  {
    id: 'relic_choice',
    maxLevel: 2,
    baseCost: 300,
    costGrowth: 3,
    /** +1 relique proposée à chaque repos. */
    step: 1,
    tag: 'survival',
  },
  {
    id: 'run_purse',
    maxLevel: 5,
    baseCost: 50,
    costGrowth: 1.8,
    /** +120 or de run au départ par niveau. */
    step: 120,
    tag: 'economy',
  },
]

const UPGRADE_BY_ID = new Map(SANCT_UPGRADES.map((u) => [u.id, u]))

export function getSanctUpgrade(id) {
  return UPGRADE_BY_ID.get(id) ?? null
}

function clampLevel(def, level) {
  const n = Math.floor(Number(level) || 0)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.min(def.maxLevel, n)
}

/** Coût pour passer du niveau `level` au niveau `level + 1` (null si déjà au max). */
export function sanctUpgradeCost(id, level) {
  const def = getSanctUpgrade(id)
  if (!def) return null
  const cur = clampLevel(def, level)
  if (cur >= def.maxLevel) return null
  return Math.round(def.baseCost * Math.pow(def.costGrowth, cur))
}

/** Éclats déjà investis pour atteindre `level` sur cette amélioration. */
export function sanctUpgradeSpent(id, level) {
  const def = getSanctUpgrade(id)
  if (!def) return 0
  const cur = clampLevel(def, level)
  let total = 0
  for (let n = 0; n < cur; n++) total += Math.round(def.baseCost * Math.pow(def.costGrowth, n))
  return total
}

/** Normalise un enregistrement de niveaux venu des stats (client ou base). */
export function normalizeSanctUpgradeLevels(raw) {
  const out = {}
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
  for (const def of SANCT_UPGRADES) {
    const lvl = clampLevel(def, src[def.id])
    if (lvl > 0) out[def.id] = lvl
  }
  return out
}

export function sanctUpgradesSpentTotal(levels) {
  const norm = normalizeSanctUpgradeLevels(levels)
  let total = 0
  for (const [id, lvl] of Object.entries(norm)) total += sanctUpgradeSpent(id, lvl)
  return total
}

/**
 * Fusion monotone : un niveau ne peut que monter. Retourne les niveaux retenus et le coût
 * des niveaux réellement ajoutés, à confronter au budget d'éclats côté serveur.
 */
export function mergeSanctUpgradeLevels(prev, next) {
  const a = normalizeSanctUpgradeLevels(prev)
  const b = normalizeSanctUpgradeLevels(next)
  const merged = {}
  let addedCost = 0
  for (const def of SANCT_UPGRADES) {
    const before = a[def.id] ?? 0
    const after = Math.max(before, b[def.id] ?? 0)
    if (after > 0) merged[def.id] = after
    if (after > before) addedCost += sanctUpgradeSpent(def.id, after) - sanctUpgradeSpent(def.id, before)
  }
  return { levels: merged, addedCost }
}

function levelOf(levels, id) {
  const norm = normalizeSanctUpgradeLevels(levels)
  return norm[id] ?? 0
}

/** Effets agrégés, appliqués au lancement d'une run. */
export function sanctUpgradeEffects(levels) {
  const yieldLvl = levelOf(levels, 'shard_yield')
  const powerLvl = levelOf(levels, 'descent_power')
  return {
    shardMult: 1 + yieldLvl * 0.08,
    dpsMult: 1 + powerLvl * 0.06,
    extraHearts: levelOf(levels, 'max_heart'),
    extraRerolls: levelOf(levels, 'draft_reroll'),
    extraRestChoices: levelOf(levels, 'relic_choice'),
    startGold: levelOf(levels, 'run_purse') * 120,
  }
}

/** Multiplicateur d'éclats maximal atteignable, pour le plafond anti-triche serveur. */
export function maxSanctShardMult() {
  const def = getSanctUpgrade('shard_yield')
  return 1 + (def?.maxLevel ?? 0) * (def?.step ?? 0)
}

function modifierShardMult(modifierIds) {
  const ids = Array.isArray(modifierIds) ? modifierIds : []
  return ids.includes('run_shard_20') ? 1.2 : 1
}

/**
 * Récompense de profondeur : surlinéaire, pour que chaque étage supplémentaire vaille
 * le risque de perdre la run plutôt que de s'arrêter au premier palier confortable.
 */
export function shardsForDepth(floorReached, modifierIds) {
  const f = Math.max(0, Math.floor(Number(floorReached) || 0))
  if (f <= 0) return 0
  const base = 4 * f + 0.6 * Math.pow(f, 1.8)
  return Math.max(0, Math.floor(base * modifierShardMult(modifierIds)))
}

/** Récompense finale d'une run. Même fonction côté client (affichage) et serveur (plafond). */
export function computeDescenteShardReward(input) {
  const mods = (Array.isArray(input?.modifierIds) ? input.modifierIds : []).filter((m) =>
    DESCENTE_KNOWN_MODIFIERS.includes(m)
  )
  const base = shardsForDepth(input?.floorReached ?? 0, mods)
  const ascKey = Math.max(0, Math.min(2, Math.floor(Number(input?.ascension) || 0)))
  const asc = DESCENTE_ASCENSION_SHARD_MULT[ascKey] ?? 1
  const preset = DESCENTE_PRESET_SHARD_MULT[input?.balancePreset] ?? 1
  const meta = Math.max(1, Math.min(maxSanctShardMult(), Number(input?.metaShardMult) || 1))
  const daily = input?.dailyBonus ? DESCENTE_DAILY_SHARD_MULT : 1
  return Math.max(0, Math.floor(base * asc * preset * meta * daily))
}

/** Récompense maximale théorique pour une profondeur donnée (toutes options au max). */
export function maxDescenteShardRewardForFloor(floorReached) {
  return computeDescenteShardReward({
    floorReached,
    modifierIds: ['run_shard_20'],
    ascension: 2,
    balancePreset: 'hardcore_event',
    metaShardMult: maxSanctShardMult(),
    dailyBonus: true,
  })
}
